import React from 'react';
import '@/styles/brand-lockup.css';

/**
 * Brand — the ONE implementation of the Keystone mark and the KEY-MADE wordmark.
 *
 * Design pass 1 part D. Before this there were five raw `/logo.png` usages
 * (LoginPage, RegisterPage, JoinPage, MoodBoardPage, plus the mood-board PDF
 * watermark) and the wordmark was loose text in GlobalFooter and AIChadBanner.
 * Nothing enforced that either survived a redesign, a collapsed rail or a phone
 * viewport.
 *
 * Rules this component exists to hold:
 *   - the mark is never optional and never hidden at any viewport;
 *   - in a collapsed rail the mark stays and the wordmark may drop to the
 *     wordmark alone (variant="rail");
 *   - on phone both appear (the wordmark shrinks, it does not disappear).
 *
 * scripts/check-brand-lock.js fails the build if any other file renders the
 * logo asset directly.
 */
const SIZES = {
  sm: { img: 'w-6 h-6', text: 'text-sm' },
  md: { img: 'w-8 h-8', text: 'text-base' },
  nav: { img: 'w-10 h-10', text: 'text-base' },
  lg: { img: 'w-16 h-16', text: 'text-2xl' },
};

export const BRAND_ASSET = '/logo.png';
export const BRAND_WORDMARK = 'KEY-MADE';

/**
 * DP32 §4 — the wordmark. KEY-MADE renders as TWO COLOURS everywhere without
 * exception: "KEY" and the hyphen in --km-key, "MADE" in --km-accent, on the
 * display face at weight 500 and 0.08em. There is no one-colour variant.
 */
export const Wordmark = ({ className = '', testId = 'brand-wordmark' }) => (
  <span className={`km-wordmark ${className}`} data-testid={testId}>
    <span className="km-wordmark__key">KEY-</span>
    <span className="km-wordmark__made">MADE</span>
  </span>
);

/**
 * DP32 §4 — the lockup: the credit line above the wordmark. "ENGINEERED BY" is
 * a CREDIT, not a heading — 8.5px, 0.16em, --km-ink-3 (--km-credit-ink-dark on
 * a dark ground), the same weight and tone as the version number.
 */
export const BrandLockup = ({ className = '', onDark = false, testId = 'brand-lockup' }) => (
  <span className={`km-lockup ${className}`} data-on-dark={onDark ? 'true' : 'false'}
        data-testid={testId}>
    <span className="km-lockup__credit" data-testid={`${testId}-credit`}>Engineered by</span>
    <Wordmark testId={`${testId}-wordmark`} />
  </span>
);

export const Brand = ({
  size = 'md',
  variant = 'full',        // 'full' | 'mark' | 'rail'
  className = '',
  wordmarkClassName = '',
  testId = 'brand',
}) => {
  const s = SIZES[size] || SIZES.md;
  const showWordmark = variant !== 'mark';

  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid={testId}>
      <img
        src={BRAND_ASSET}
        alt={BRAND_WORDMARK}
        className={`${s.img} object-contain shrink-0`}
        data-testid={`${testId}-mark`}
      />
      {showWordmark && (
        <span className={`${s.text} ${wordmarkClassName}`} data-testid={`${testId}-wordmark`}>
          <Wordmark testId={`${testId}-wordmark-text`} />
        </span>
      )}
    </span>
  );
};

/** Mark only — for a collapsed rail. The mark still never disappears. */
export const BrandMark = (props) => <Brand {...props} variant="mark" />;

export default Brand;
